import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, update } from 'firebase/database';
import { auth, db } from './firebase';
import { logAdminAction } from './auditLog';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

export default function RefundTab({ orders }) {
  const [refunds, setRefunds]           = useState([]);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState('eligible');
  const [showModal, setShowModal]       = useState(null);
  const [refundMethod, setRefundMethod] = useState('momo');
  const [refundNote, setRefundNote]     = useState('');
  const [sending, setSending]           = useState(false);

  useEffect(() => {
    onValue(ref(db, 'refunds'), snap => {
      setRefunds(snap.exists()
        ? Object.entries(snap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : []);
    });
  }, []);

  const refundedOrderIds = new Set(refunds.map(r => r.orderId));

  const eligibleOrders = orders.filter(o =>
    (o.status === 'cancelled' || o.status === 'notReceived' || o.refundRequested === true)
    && !refundedOrderIds.has(o.id)
  );

  const processedOrders = orders.filter(o => refundedOrderIds.has(o.id));

  const searchFilter = list => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(o =>
      (o.customerName  || '').toLowerCase().includes(q) ||
      (o.orderId || o.id).toLowerCase().includes(q) ||
      (o.customerEmail || '').toLowerCase().includes(q) ||
      (o.customerPhone || '').includes(search)
    );
  };

  const displayList =
    filter === 'eligible'  ? searchFilter(eligibleOrders) :
    filter === 'processed' ? searchFilter(processedOrders) :
    searchFilter([...eligibleOrders, ...processedOrders]);

  const totalRefunded  = refunds.reduce((s, r) => s + (r.amount || 0), 0);
  const pendingCount   = eligibleOrders.length;

  const REFUND_METHODS = [
    { id: 'momo',    label: '📱 MTN MoMo',       sub: 'Send to customer MoMo number' },
    { id: 'telecel', label: '📱 Telecel Cash',    sub: 'Send to Telecel number' },
    { id: 'bank',    label: '🏦 Bank Transfer',   sub: 'Send to customer bank account' },
    { id: 'wallet',  label: '💳 PharmDev Wallet', sub: 'Add credit to customer wallet' },
  ];

  const REASON_STYLE = {
    cancelled:       { icon: '✕',  label: 'Order Cancelled',   color: '#B91C1C', bg: '#FEE2E2' },
    notReceived:     { icon: '📭', label: 'Not Received',      color: '#92400E', bg: '#FEF3C7' },
    refundRequested: { icon: '🔔', label: 'Refund Requested',  color: '#1D4ED8', bg: '#EFF6FF' },
  };

  const getReason = o =>
    o.status === 'cancelled'    ? REASON_STYLE.cancelled :
    o.status === 'notReceived'  ? REASON_STYLE.notReceived :
    REASON_STYLE.refundRequested;

  const processRefund = async order => {
    if (!refundNote.trim()) { alert('Please add a refund note before processing.'); return; }
    const paidByWallet = order.paymentMethod === 'wallet';
    setSending(true);
    try {
      // Actually reverse the charge before recording anything as
      // "refunded" — a DB record with no real money movement would tell
      // the customer their money is back when it isn't. A wallet-paid
      // order has no real Paystack transaction behind it, so it goes back
      // to the wallet directly instead of through Paystack.
      const token = await auth.currentUser.getIdToken();
      const refundRes = await fetch(`${BACKEND_URL}${paidByWallet ? '/wallet/refund' : '/paystack/refund'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderId: order.id }),
      });
      const refundData = await refundRes.json();
      if (!refundRes.ok) throw new Error(refundData.error || (paidByWallet ? 'Wallet refund failed' : 'Paystack refund failed'));

      const payoutRef = push(ref(db, 'refunds'));
      await set(payoutRef, {
        orderId:         order.id,
        orderRef:        (order.orderId || order.id).slice(-6).toUpperCase(),
        customerId:      order.customerId     || '',
        customerName:    order.customerName   || 'Customer',
        customerEmail:   order.customerEmail  || '',
        customerPhone:   order.customerPhone  || '',
        amount:          order.total          || 0,
        reason:          order.status === 'cancelled'   ? 'Order cancelled' :
                         order.status === 'notReceived' ? 'Order not received' :
                         'Customer refund request',
        method:          refundMethod,
        note:            refundNote.trim(),
        status:          'processed',
        paystackRefundReference: refundData.refundReference || '',
        paystackRefundStatus:    refundData.status || '',
        processedBy:     auth.currentUser?.email || 'admin',
        pharmacy:        order.items?.[0]?.pharmacy || '—',
        items:           order.items?.map(i => i.name).join(', ') || '—',
        createdAt:       new Date().toISOString(),
      });

      await update(ref(db, `orders/${order.id}`), {
        refundStatus:      'refunded',
        refundedAt:        new Date().toISOString(),
        refundMethod,
        refundNote:        refundNote.trim(),
        refundProcessedBy: auth.currentUser?.email || 'admin',
      });

      await logAdminAction('process_refund', order.customerName || order.id, { orderId: order.id, amount: order.total, method: refundMethod });

      setShowModal(null);
      setRefundNote('');
      setRefundMethod('momo');
      alert(paidByWallet
        ? `✅ GH₵ ${order.total?.toFixed(2)} credited back to ${order.customerName || 'customer'}'s wallet.`
        : `✅ Refund of GH₵ ${order.total?.toFixed(2)} sent via Paystack for ${order.customerName || 'customer'} (status: ${refundData.status || 'pending'}).`);
    } catch (e) {
      alert('Refund failed: ' + (e.message || 'Please try again.'));
    } finally { setSending(false); }
  };

  const inp = {
    padding: '9px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
    fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none',
  };

  return (
    <div>

      {/* ── REFUND MODAL ─────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, margin: 0, fontSize: 17 }}>↩ Process Refund</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={() => { setShowModal(null); setRefundNote(''); }}>✕</button>
            </div>

            {/* ORDER SUMMARY */}
            <div style={{ backgroundColor: '#F7F8FA', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #E5E7EB' }}>
              {[
                ['Order #',   (showModal.orderId || showModal.id).slice(-6).toUpperCase()],
                ['Customer',  showModal.customerName  || '—'],
                ['Phone',     showModal.customerPhone || '—'],
                ['Email',     showModal.customerEmail || '—'],
                ['Medicines', showModal.items?.map(i => `${i.name} x${i.quantity}`).join(', ') || '—'],
                ['Payment',   showModal.paymentMethod || 'MoMo'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid #E5E7EB' }}>
                  <span style={{ fontSize: 13, color: '#888' }}>{l}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, maxWidth: '60%', textAlign: 'right' }}>{v}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Refund Amount</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#EF4444' }}>GH₵ {showModal.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* REFUND METHOD */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 4 }}>
                Refund Method
              </label>
              <p style={{ fontSize: 11, color: '#888', margin: '0 0 10px' }}>
                For your records only — Paystack always returns the money to the customer's original payment method automatically.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {REFUND_METHODS.map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${refundMethod === m.id ? '#1D9E75' : '#E5E7EB'}`, backgroundColor: refundMethod === m.id ? '#E8F8F2' : '#FFF', cursor: 'pointer' }}>
                    <input type="radio" checked={refundMethod === m.id} onChange={() => setRefundMethod(m.id)} style={{ accentColor: '#1D9E75', marginTop: 2 }} />
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: '0 0 2px' }}>{m.label}</p>
                      <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{m.sub}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* REFUND NOTE */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 6 }}>
                Refund Note <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <textarea
                style={{ ...inp, width: '100%', boxSizing: 'border-box', minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder={`e.g. Order #${(showModal.orderId || showModal.id).slice(-6).toUpperCase()} was cancelled because the pharmacy was closed. Full refund of GH₵ ${showModal.total?.toFixed(2)} sent to customer MTN MoMo.`}
                value={refundNote}
                onChange={e => setRefundNote(e.target.value)}
              />
              <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                This note is saved with the refund record and visible in refund history.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '12px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }}
                onClick={() => { setShowModal(null); setRefundNote(''); }}>
                Cancel
              </button>
              <button
                style={{ flex: 2, padding: '12px', backgroundColor: sending ? '#FCA5A5' : '#EF4444', color: '#FFF', border: 'none', borderRadius: 10, cursor: sending ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14 }}
                onClick={() => processRefund(showModal)}
                disabled={sending}
              >
                {sending ? 'Processing...' : `↩ Refund GH₵ ${showModal.total?.toFixed(2)} to ${showModal.customerName || 'customer'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PAGE HEADER ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>↩ Customer Refunds</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Manage refunds for cancelled and undelivered orders
          </p>
        </div>
      </div>

      {/* ── STATS ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Pending Refunds',   value: pendingCount,                                        color: '#EF4444', icon: '⏳', sub: 'Awaiting processing' },
          { label: 'Processed',         value: refunds.length,                                      color: '#1D9E75', icon: '✅', sub: 'All time' },
          { label: 'Total Refunded',    value: `GH₵ ${totalRefunded.toFixed(2)}`,                  color: '#F59E0B', icon: '💸', sub: 'All time' },
          { label: 'Cancelled Orders',  value: orders.filter(o => o.status === 'cancelled').length, color: '#6B7280', icon: '✕',  sub: 'Total cancelled' },
        ].map((st, i) => (
          <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB', borderTop: `4px solid ${st.color}`, textAlign: 'center' }}>
            <p style={{ fontSize: 24, margin: '0 0 6px' }}>{st.icon}</p>
            <p style={{ fontSize: 22, fontWeight: 800, color: st.color, margin: '0 0 3px' }}>{st.value}</p>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: '0 0 2px' }}>{st.label}</p>
            <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{st.sub}</p>
          </div>
        ))}
      </div>

      {/* ── PENDING ALERT ────────────────────────────────────────────────── */}
      {pendingCount > 0 && (
        <div style={{ backgroundColor: '#FEF3C7', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #FCD34D', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 26 }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 700, color: '#92400E', margin: '0 0 2px', fontSize: 14 }}>
              {pendingCount} customer{pendingCount > 1 ? 's' : ''} waiting for a refund
            </p>
            <p style={{ color: '#92400E', fontSize: 13, margin: 0 }}>
              These orders were cancelled or not received. Process their refunds promptly to maintain customer trust.
            </p>
          </div>
        </div>
      )}

      {/* ── SEARCH + FILTER ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...inp, width: 280 }}
          placeholder="Search customer, order ID, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4 }}>
          {[
            { id: 'eligible',  label: `⏳ Pending (${pendingCount})` },
            { id: 'processed', label: `✅ Processed (${refunds.length})` },
            { id: 'all',       label: 'All' },
          ].map(tab => (
            <button key={tab.id}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filter === tab.id ? 700 : 400, backgroundColor: filter === tab.id ? '#FFF' : 'transparent', color: filter === tab.id ? '#1D9E75' : '#888' }}
              onClick={() => setFilter(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>
        {search && (
          <button style={{ padding: '7px 12px', backgroundColor: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}
            onClick={() => setSearch('')}>✕ Clear</button>
        )}
      </div>

      {/* ── ORDER CARDS ──────────────────────────────────────────────────── */}
      {displayList.length === 0 ? (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', textAlign: 'center', padding: 60 }}>
          <p style={{ fontSize: 48, marginBottom: 12 }}>✅</p>
          <p style={{ fontWeight: 700, color: '#1A1A1A', fontSize: 16, margin: '0 0 6px' }}>
            {filter === 'eligible' ? 'No pending refunds' : filter === 'processed' ? 'No refunds processed yet' : 'No refunds found'}
          </p>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            {filter === 'eligible' ? 'Great work — all eligible orders have been refunded!' : 'Refunds will appear here once processed.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))', gap: 14 }}>
          {displayList.map(order => {
            const isRefunded     = refundedOrderIds.has(order.id);
            const refundRecord   = refunds.find(r => r.orderId === order.id);
            const reason         = getReason(order);

            return (
              <div key={order.id} style={{ backgroundColor: '#FFF', borderRadius: 12, border: `1px solid ${isRefunded ? '#86EFAC' : '#FCA5A5'}`, overflow: 'hidden', borderTop: `4px solid ${isRefunded ? '#1D9E75' : '#EF4444'}` }}>

                {/* CARD HEADER */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: isRefunded ? '#F0FDF4' : '#FFF5F5' }}>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 2px', color: '#1D9E75' }}>
                      #{(order.orderId || order.id).slice(-6).toUpperCase()}
                    </p>
                    <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={{ backgroundColor: reason.bg, color: reason.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {reason.icon} {reason.label}
                    </span>
                    {isRefunded && (
                      <span style={{ backgroundColor: '#DCFCE7', color: '#15803D', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                        ✅ Refunded
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ padding: 16 }}>

                  {/* CUSTOMER DETAILS */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {[
                      ['Customer',  order.customerName  || '—'],
                      ['Phone',     order.customerPhone || '—'],
                      ['Email',     order.customerEmail || '—'],
                      ['Payment',   order.paymentMethod || 'MoMo'],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <p style={{ fontSize: 11, color: '#888', margin: '0 0 2px' }}>{l}</p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: 0 }}>{v}</p>
                      </div>
                    ))}
                  </div>

                  {/* MEDICINES */}
                  <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: '8px 12px', marginBottom: 12, border: '1px solid #E5E7EB' }}>
                    <p style={{ fontSize: 11, color: '#888', margin: '0 0 4px' }}>Medicines ordered</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', margin: 0 }}>
                      {order.items?.map(i => `${i.name} x${i.quantity}`).join(' · ') || '—'}
                    </p>
                  </div>

                  {/* REFUND AMOUNT */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A' }}>Refund Amount</span>
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#EF4444' }}>GH₵ {order.total?.toFixed(2)}</span>
                  </div>

                  {/* REFUND RECORD (if already done) */}
                  {isRefunded && refundRecord && (
                    <div style={{ backgroundColor: '#E8F8F2', borderRadius: 8, padding: 12, border: '1px solid #B2DFD0', marginBottom: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: '#1D6B52', margin: '0 0 8px' }}>✅ Refund Record</p>
                      {[
                        ['Method',    refundRecord.method === 'momo' ? '📱 MTN MoMo' : refundRecord.method === 'telecel' ? '📱 Telecel Cash' : refundRecord.method === 'bank' ? '🏦 Bank Transfer' : '💳 Wallet'],
                        ['Processed', new Date(refundRecord.createdAt).toLocaleString()],
                        ['By',        refundRecord.processedBy],
                      ].map(([l, v]) => (
                        <p key={l} style={{ fontSize: 12, color: '#555', margin: '0 0 3px' }}>
                          <strong>{l}:</strong> {v}
                        </p>
                      ))}
                      {refundRecord.note && (
                        <p style={{ fontSize: 12, color: '#1D6B52', margin: '6px 0 0', fontStyle: 'italic', lineHeight: 1.5 }}>
                          📋 {refundRecord.note}
                        </p>
                      )}
                    </div>
                  )}

                  {/* ACTION BUTTON */}
                  {!isRefunded ? (
                    <button
                      style={{ width: '100%', padding: '12px', backgroundColor: '#EF4444', color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                      onClick={() => { setShowModal(order); setRefundNote(''); setRefundMethod('momo'); }}
                    >
                      ↩ Process Refund — GH₵ {order.total?.toFixed(2)}
                    </button>
                  ) : (
                    <button disabled style={{ width: '100%', padding: '11px', backgroundColor: '#F7F8FA', color: '#888', border: '1px solid #E5E7EB', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'default' }}>
                      ✅ Refund already processed
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── REFUND HISTORY TABLE ─────────────────────────────────────────── */}
      {refunds.length > 0 && (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', marginTop: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>
              📜 Refund History ({refunds.length})
            </p>
            <p style={{ fontSize: 13, color: '#EF4444', fontWeight: 700, margin: 0 }}>
              Total refunded: GH₵ {totalRefunded.toFixed(2)}
            </p>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Order #', 'Customer', 'Phone', 'Amount', 'Method', 'Reason', 'Note', 'Date', 'Processed By'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F7F8FA', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {refunds.map((r, i) => (
                  <tr key={r.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1D9E75' }}>#{r.orderRef}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{r.customerName}</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{r.customerPhone || '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 800, color: '#EF4444' }}>GH₵ {r.amount?.toFixed(2)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {r.method === 'momo'    ? '📱 MoMo'   :
                       r.method === 'telecel' ? '📱 Telecel' :
                       r.method === 'bank'    ? '🏦 Bank'    : '💳 Wallet'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#888' }}>{r.reason}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.note}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: '#888' }}>{r.processedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}